<template>
  <div data-testid="suggested-merges-table">
    <AppScrollableTable
      class="table-rounded table-bordered"
      :has-fixed-column="false"
    >
      <AppTable
        :table-data="displayRows"
        :loading="loading"
      >
        <template #empty>
          <div
            class="pt-30 table-empty pb-20 text-center merge-empty-state"
            data-testid="suggested-empty-state"
          >
            <img
              :src="IMAGE_EMPTY_BOX"
              class="mb-12"
              alt="no suggested members"
            />

            <h5>No suggested members</h5>
            <p class="text-muted">
              We can't find any matches based on username<br />
              or full names of existing members
            </p>
          </div>
        </template>
        <AppTableColumn name="dup">
          <template #header> Duplicate member <span class="text-muted">(merge from)</span> </template>
          <template #default="{ row }">
            <div class="d-flex align-items-center gap-5">
              <HubAvatar
                :user-id="row.duplicate.user.id"
                avatar-style="medium"
                :users-avatars="usersAvatars"
              />
              <div>
                <div
                  data-testid="suggested-dup-name"
                  :data-user-id="row.duplicate.user.id"
                >
                  {{ row.duplicate.user.name }}
                </div>
                <div class="small text-lighter">{{ row.duplicate.os_username }}</div>
                <div
                  class="small text-lighter"
                  data-testid="suggested-dup-email"
                >
                  {{ row.duplicate.user.email }}
                </div>
              </div>
            </div>
          </template>
        </AppTableColumn>
        <AppTableColumn
          header="Billing status"
          name="dup_billing"
          :header-tooltip="billingGraceTooltip"
        >
          <template #default="{ row }">
            <div class="d-flex">
              <div class="d-flex flex-column align-items-center">
                <span
                  :class="[
                    'label',
                    row.duplicate.billing_status === 'Billed' ? 'label-success-lighter' : 'label-danger-lighter',
                  ]"
                >
                  {{ row.duplicate.billing_status }}
                </span>
                <div
                  v-if="row.duplicate.days_left"
                  class="small text-lighter mt-5"
                >
                  {{ row.duplicate.days_left }} {{ $filters.pluralize(row.duplicate.days_left, 'day') }} left
                </div>
              </div>
            </div>
          </template>
        </AppTableColumn>
        <AppTableColumn
          :header-classes="pagedRows.length === 0 ? 'hidden' : ''"
          cell-classes="merge-into-arrow-column"
        >
          <i class="fa hi-right-arrow" />
        </AppTableColumn>
        <AppTableColumn name="main">
          <template #header> Main member <span class="text-muted">(merge to)</span> </template>
          <template #default="{ row }">
            <div class="d-flex align-items-center gap-5">
              <HubAvatar
                :user-id="row.main.user.id"
                avatar-style="medium"
                :users-avatars="usersAvatars"
              />
              <div>
                <div
                  data-testid="suggested-main-name"
                  :data-user-id="row.main.user.id"
                >
                  {{ row.main.user.name }}
                </div>
                <div class="small text-lighter">{{ row.main.os_username }}</div>
                <div
                  class="small text-lighter"
                  data-testid="suggested-main-email"
                >
                  {{ row.main.user.email }}
                </div>
              </div>
            </div>
          </template>
        </AppTableColumn>
        <AppTableColumn
          header="Billing status"
          name="main_billing"
          :header-tooltip="billingGraceTooltip"
        >
          <template #default="{ row }">
            <div class="d-flex">
              <div class="d-flex flex-column align-items-center">
                <span
                  :class="[
                    'label',
                    row.main.billing_status === 'Billed' ? 'label-success-lighter' : 'label-danger-lighter',
                  ]"
                >
                  {{ row.main.billing_status }}
                </span>
                <div
                  v-if="row.main.days_left"
                  class="small text-lighter mt-5"
                >
                  {{ row.main.days_left }} {{ $filters.pluralize(row.main.days_left, 'day') }} left
                </div>
              </div>
            </div>
          </template>
        </AppTableColumn>
        <AppTableColumn
          header="Actions"
          name="actions"
        >
          <template #default="{ row }">
            <AppButton
              size="small"
              modifiers="decline-merge"
              data-testid="suggested-decline-button"
              :disabled="decliningIds.includes(row.recommendation_id)"
              @click="declineRow(row)"
            >
              Decline
            </AppButton>
            <AppButton
              size="small"
              class="mr-5"
              data-testid="suggested-merge-button"
              @click="openConfirm(row)"
            >
              Merge
            </AppButton>
          </template>
        </AppTableColumn>
      </AppTable>
    </AppScrollableTable>

    <AppPagination
      v-if="paginationPayload && paginationPayload.total_pages > 1"
      :pagination="paginationPayload"
      :resource="{ one: 'member', many: 'members' }"
      @page-changed="onPageChanged"
    />

    <ClientAgentMergeConfirmDialog
      v-if="confirmRow"
      :duplicate="confirmRow.duplicate"
      :main="confirmRow.main"
      @cancel="confirmRow = null"
      @confirm="handleConfirm"
    />
  </div>
</template>

<script>
import ClientAgentMergeConfirmDialog from '~/components/client_agents/ClientAgentMergeConfirmDialog.vue';
import * as Resources from '~/resources';
import { scrollToTop } from '~/utils/scroll_utils';
import { IMAGE_EMPTY_BOX } from '~/rails_assets';
import { createUsersAvatarsMixin } from '~/mixins/UsersAvatarsMixin';
import CorpAppBillingMixin from '~/mixins/CorpAppBillingMixin';

export default {
  name: 'SuggestedMergesTable',
  components: { ClientAgentMergeConfirmDialog },
  mixins: [createUsersAvatarsMixin('avatarUsers'), CorpAppBillingMixin],
  props: {
    organizationId: { type: [String, Number], required: true },
    mergingUserIds: { type: Array, default: () => [] },
  },
  data() {
    return {
      rows: [],
      loading: false,
      merging: false,
      decliningIds: [],
      page: 1,
      perPage: 20,
      totalCount: 0,
      IMAGE_EMPTY_BOX,
      confirmRow: null,
      paginationPayload: null,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalCount / this.perPage) || 1;
    },
    pagedRows() {
      return this.rows;
    },
    displayRows() {
      return this.pagedRows.map((r) => {
        const disabledByClient =
          this.mergingUserIds.includes(r.duplicate.user.id) || this.mergingUserIds.includes(r.main.user.id);
        const disabledByServer = r.duplicate.dup_merge_in_progress || r.main.main_merge_in_progress;

        if (disabledByServer || disabledByClient) {
          return { ...r, trClasses: ['row-merging'] };
        }

        return r;
      });
    },
    avatarUsers() {
      return this.rows.flatMap((r) => [
        { id: r.duplicate.user.id, avatar: r.duplicate.user.avatar_url },
        { id: r.main.user.id, avatar: r.main.user.avatar_url },
      ]);
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const params = { page: this.page, per_page: this.perPage };
        const res = await Resources.OrganizationsResource.mergeMembersRecommendations(
          { id: this.organizationId },
          params,
        );

        this.rows = res.suggested_merges || [];
        this.paginationPayload = res.pagination || null;
        this.totalCount = this.paginationPayload ? this.paginationPayload.total_count : this.rows.length;

        const lastPage = this.totalPages;

        if (this.page > lastPage) {
          this.page = lastPage;
          await this.fetchData();
        }
      } catch (e) {
        console.error(e);
        this.$appMessage('Something went wrong. Please try again.');
      } finally {
        this.loading = false;
        this.$emit('reload');
      }
    },
    onPageChanged(page) {
      this.page = page;
      this.fetchData().then(() => {
        this.$nextTick(() => {
          scrollToTop(this.$el);
        });
      });
    },
    async declineRow(row) {
      const recommendationId = row.recommendation_id;

      if (this.decliningIds.includes(recommendationId)) {
        return;
      }
      this.decliningIds = [...this.decliningIds, recommendationId];

      try {
        await Resources.OrganizationsResource.declineMergeRecommendation({
          id: this.organizationId,
          recommendation_id: recommendationId,
        });
        window.analytics?.track('Merge suggestion declined', {
          recommendation_id: recommendationId,
          from_user_id: row.duplicate.user.id,
          to_user_id: row.main.user.id,
        });
        await this.fetchData();
        this.$appMessage('Merge recommendation declined');
      } catch (e) {
        console.error(e);
        this.$appMessage('Something went wrong. Please try again later.');
      } finally {
        this.decliningIds = this.decliningIds.filter((id) => id !== recommendationId);
      }
    },
    openConfirm(row) {
      this.confirmRow = row;
    },
    async handleConfirm() {
      if (!this.confirmRow) {
        return;
      }
      if (this.merging) {
        return;
      }
      this.merging = true;

      const fromIds = [this.confirmRow.duplicate.user.id];
      const toId = this.confirmRow.main.user.id;
      const recommendationId = this.confirmRow.recommendation_id;

      try {
        await Resources.OrganizationsResource.mergeMembers(
          { id: this.organizationId },
          { from_user_ids: fromIds, to_user_id: toId, recommendation_id: recommendationId },
        );
        this.$emit('merge-started', { fromIds, toId });
      } catch (e) {
        console.error(e);
        this.$appMessage('Something went wrong when we tried to merge these members. Please try again later.', {
          header: 'Merge failed',
        });
      } finally {
        this.merging = false;
        this.confirmRow = null;
      }
    },
  },
};
</script>
